import { FC, useEffect } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { IonCard } from '@ionic/react';
import ApiError from '../../ApiError/ApiError';
import SeptimLoadingSpinner from '../SeptimLoadingSpinner/SeptimLoadingSpinner';
import EditorHeader from './components/EditorHeader/EditorHeader';
import EditorBody from './components/EditorBody/EditorBody';
import EditorPanel from './components/EditorPanel/EditorPanel';
import { useLayoutEditorContext } from '../../../hooks/context/useLayoutEditorContext';
import { useUnsavedChangesGuard } from '../../../hooks/useUnsavedChangesGuard';
import { useFocusedFieldOutsideRelease } from './hooks/useFocusedFieldOutsideRelease';

const LayoutEditorContent: FC = () => {
  const history = useHistory();
  // URL of the editor route match — navigation inside it (fullscreen reference)
  // is editor-internal and must not trigger the unsaved-changes guard.
  const { url: editorUrl } = useRouteMatch();
  const {
    layout,
    isLoading,
    error,
    isDirty,
    saveLayout,
    fullscreenReference,
    closeFullscreenReference,
  } = useLayoutEditorContext();

  // Any way out of the editor with unsaved changes: in-app navigation opens the
  // save/stay modal, browser reload/close gets the native prompt.
  const { modalElement } = useUnsavedChangesGuard({
    isDirty,
    onSave: saveLayout,
    allowedPathPrefix: editorUrl,
  });

  // Pointer-down anywhere outside the focused field's panel item drops the
  // field focus and its canvas overlay.
  useFocusedFieldOutsideRelease();

  // The reference breadcrumb link navigates back without closing the picker —
  // drop the stale picker state so a later direct reference URL can't show it.
  useEffect(() => {
    if (fullscreenReference) {
      closeFullscreenReference();
    }
  }, []);

  // The guard owns the dirty handling — a plain back is enough here.
  const handleBack = (): void => {
    history.goBack();
  };

  if (error) {
    return (
      <div className="page-container">
        <IonCard>
          <ApiError error={error} />
        </IonCard>
      </div>
    );
  }

  if (isLoading && !layout) {
    return (
      <div className="page-container layout-editor-loading">
        <SeptimLoadingSpinner />
      </div>
    );
  }

  return (
    <div className="page-container layout-editor-page">
      <IonCard className="layout-editor">
        <EditorHeader onBack={handleBack} />
        <div className="layout-editor-content">
          <EditorBody />
          <EditorPanel />
        </div>
      </IonCard>
      {modalElement}
    </div>
  );
};

export default LayoutEditorContent;
